import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Paper, FormControlLabel, Checkbox, Alert, Chip, Grid, Divider, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { getData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';
import EmployeeExitDetails from './employeeExitDetails';
import EmployeeRehire from './employeeRehire';

const ExitRehire = ({
    initialData,
    isRehireMode,
    onStartRehire,
    rehireChecklist,
    rehireDetails,
    onNavigateToTab,
    onUpdateRehireChecklist,
    onUpdateRehireDetails,
}) => {
    const [searchParams] = useSearchParams();
    const empIdFromUrl = searchParams.get("employeeId");
    const [exitDetailsData, setExitDetailsData] = useState(null);
    const [rehireDetailsData, setRehireDetailsData] = useState(null);
    const [isTerminationMode, setIsTerminationMode] = useState(false);
    const [exitStatus, setExitStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const exitDetails = exitDetailsData?.[0] || null;
    const apiEmployeeStatus = exitDetails?.employee_status;
    const actualEmployeeStatus = apiEmployeeStatus || initialData?.status || 'inactive';
    const isEmployeeInactive = actualEmployeeStatus === 'inactive';
    const isEmployeeActive = actualEmployeeStatus === 'active';
    const isTerminationInProgress = exitStatus === 'Initiate' || exitStatus === 'InProgress';
    const isTerminationCompleted = exitStatus === 'Completed';

    const fetchExitDetails = useCallback(async () => {
        if (!empIdFromUrl) return;
        try {
            setLoading(true);
            const response = await getData(API_ENDPOINTS.fetchExitRehireDetails(empIdFromUrl));

            if (response?.status === 'SUCCESS') {
                const { exitDetails, personalDetails } = response.result;
                setExitDetailsData(exitDetails || null);
                setRehireDetailsData(personalDetails || null);
                const currentExitStatus = exitDetails?.[0]?.exit_status || '';
                setExitStatus(currentExitStatus);

                if (currentExitStatus === 'Initiate' || currentExitStatus === 'InProgress') {
                    setIsTerminationMode(true);
                } else {
                    setIsTerminationMode(false);
                }
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [empIdFromUrl]);

    useEffect(() => {
        fetchExitDetails();
    }, [fetchExitDetails]);

    const handleRehireComplete = (newRehireDetails) => {
        setRehireDetailsData(newRehireDetails);
        if (onStartRehire) onStartRehire(false);
        fetchExitDetails();
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ my: 2 }}>
                <Typography variant="h6" fontWeight={600} component="h2" gutterBottom>
                    Employee Status Management
                </Typography>

                <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                    {/* ✅ Header with current employee status */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Current Employee Status:
                        </Typography>

                        <Chip
                            label={actualEmployeeStatus.toUpperCase()}
                            color={actualEmployeeStatus === 'active' ? 'success' : 'default'}
                            sx={{ fontWeight: 600 }}
                        />

                        {isTerminationInProgress && (
                            <Chip
                                label="Termination/Resignation in Process"
                                color="warning"
                                size="small"
                                sx={{ fontWeight: 600 }}
                            />
                        )}
                    </Box>

                    {/* ✅ Alert for ongoing termination */}
                    {isEmployeeActive && isTerminationInProgress && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Termination or Resignation is currently in process.</strong><br />
                            Please complete the process before initiating rehire.
                        </Alert>
                    )}

                    {/* ✅ FIXED: Termination checkbox - ONLY show if employee is ACTIVE and no termination process exists */}
                    {isEmployeeActive && !isTerminationInProgress && !isTerminationCompleted && (
                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isTerminationMode}
                                        onChange={(e) => setIsTerminationMode(e.target.checked)}
                                        color="error"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1" fontWeight={600}>
                                            Start Termination or Resignation Process
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Check this to initiate termination/resignation flow
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    )}

                    {/* ✅ Rehire checkbox - ONLY show if employee is INACTIVE and termination is completed */}
                    {!isRehireMode &&
                        isEmployeeInactive &&
                        isTerminationCompleted && (
                            <Box sx={{ mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isRehireMode}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                onStartRehire(checked);

                                                if (checked) {
                                                    onUpdateRehireChecklist({
                                                        personal: false,
                                                        employee: false,
                                                        compensation: false,
                                                        policies: false,
                                                        banking: false,
                                                        documents: false,
                                                    });
                                                    onUpdateRehireDetails({
                                                        rehireDate: '',
                                                        rehireReason: ''
                                                    });
                                                }
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1" fontWeight={600}>
                                                Start Rehire Process
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Check this to begin rehiring this employee after termination/resignation completion.
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        )}
                </Paper>

                <Box sx={{ mt: 3 }}>
                    {/* ✅ Rehire Flow */}
                    {isRehireMode ? (
                        <EmployeeRehire
                            initialData={{
                                ...initialData,
                                rehireChecklist,
                                rehireDetails
                            }}
                            userId={empIdFromUrl}
                            onNavigateToTab={onNavigateToTab}
                            onUpdateRehireChecklist={onUpdateRehireChecklist}
                            onUpdateRehireDetails={onUpdateRehireDetails}
                            onRehireComplete={handleRehireComplete}
                        />
                    ) : (
                        <>
                            {/* ✅ Show ExitProcess if termination is in progress OR user manually checked termination */}
                            {isTerminationInProgress || isTerminationMode ? (
                                <EmployeeExitDetails userId={empIdFromUrl} onUpdate={fetchExitDetails} />
                            ) : (
                                /* ✅ Show termination/rehire details ONLY when termination is completed */
                                (
                                    <>
                                        {/* ✅ Previous Termination Details Section */}
                                        {isTerminationCompleted && exitDetails && (
                                            <Box sx={{ mb: 4, p: 2, border: '1px solid #ddd', borderRadius: 2, backgroundColor: '#fafafa' }}>
                                                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                                                    Termination or Resignation Details
                                                </Typography>

                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 12, sm: 4 }}>
                                                        <Typography variant="subtitle2" color="textSecondary">Type</Typography>
                                                        <Typography variant="body1">{exitDetails.type || '-'}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, sm: 4 }}>
                                                        <Typography variant="subtitle2" color="textSecondary">Date of Notice</Typography>
                                                        <Typography variant="body1">{exitDetails.date_of_notice || '-'}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, sm: 4 }}>
                                                        <Typography variant="subtitle2" color="textSecondary">Final Date of Exit</Typography>
                                                        <Typography variant="body1">{exitDetails.final_date_of_exit || '-'}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, sm: 4 }}>
                                                        <Typography variant="subtitle2" color="textSecondary">Notice Period (Days)</Typography>
                                                        <Typography variant="body1">{exitDetails.notice_period_days || '-'}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, sm: 4 }}>
                                                        <Typography variant="subtitle2" color="textSecondary">Reason for Exit</Typography>
                                                        <Typography variant="body1">{exitDetails.reason_for_exit || '-'}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <Divider sx={{ my: 2 }} />
                                                        <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                                                        <Typography variant="body1">{exitDetails.exit_status || 'Unknown'}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}

                                        {/* ✅ Previous Rehire Details Section (if exists) */}
                                        {rehireDetailsData && (rehireDetailsData.rehire_date || rehireDetailsData.rehire_reason) && (
                                            <Box sx={{ mb: 4, p: 2, border: '1px solid #ddd', borderRadius: 2, backgroundColor: '#e8f5e9' }}>
                                                <Typography variant="h6" color="success.main" sx={{ mb: 2 }}>
                                                    Rehire Details
                                                </Typography>

                                                <Grid container spacing={2}>
                                                    {rehireDetailsData.rehire_date && (
                                                        <Grid size={{ xs: 12, sm: 4 }}>
                                                            <Typography variant="subtitle2" color="textSecondary">Rehire Date</Typography>
                                                            <Typography variant="body1">{rehireDetailsData.rehire_date}</Typography>
                                                        </Grid>
                                                    )}
                                                    {rehireDetailsData.rehire_reason && (
                                                        <Grid size={{ xs: 12, sm: 4 }}>
                                                            <Typography variant="subtitle2" color="textSecondary">Rehire Reason</Typography>
                                                            <Typography variant="body1">{rehireDetailsData.rehire_reason}</Typography>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>
                                        )}
                                    </>
                                )
                            )}

                            {/* ✅ Show empty state only when there's no active process AND no completed process */}
                            {!isTerminationInProgress && !isTerminationMode && !isTerminationCompleted && (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                                    No active termination or rehire process.
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ExitRehire;